<template>
  <div>
    <div class="page-heading">
      <h1
        v-if="!editMode"
        class="page-heading__title"
      >
        Create lounge booking
      </h1>
      <h1
        v-else
        class="page-heading__title"
      >
        Modify lounge booking
      </h1>
    </div>
    <div class="container">
      <mag-back-nav
        v-if="editMode"
        :back-path="backPath"
        class="mt-3"
      />
      <div class="row mt-4">
        <div class="col-md-8">
          <div class="row">
            <div class="col-12 mb-2">
              <SelectAirport
                :disabled="editMode || bookingReserved"
                :hide-all-option="true"
              />
            </div>
            <div class="col-12" />
            <SelectTerminal
              v-if="airportSelected"
              :disabled="bookingReserved || reserveInProgress || (editMode && paymentComplete)"
              :hide-do-not-know-option="true"
            />
          </div>
          <div
            v-if="terminalSelected"
            class="col-12 mb-2"
          >
            <SelectDateTime
              :disabled="reserveInProgress || (editMode && paymentComplete)"
              :time-zone="timeZone"
              :time-picker-config="timePickerConfig"
              :show-exit-options="false"
            />
          </div>
          <div
            v-if="entrySelected && selectedDateTimeValid"
            class="col-6 mb-2"
          >
            <div class="col-heading">
              <h3 class="mb-4">
                Booking size
              </h3>
            </div>
            <div class="card">
              <div class="row">
                <div class="col-6">
                  <ProductRequirement
                    :disabled="bookingReserved || reserveInProgress || (editMode && paymentComplete)"
                    :config="requirementConfigs[0]"
                  />
                </div>
                <div class="col-6">
                  <ProductRequirement
                    :disabled="!primaryRequirementGreaterThanZero || bookingReserved || reserveInProgress || (editMode && paymentComplete)"
                    :config="requirementConfigs[1]"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="primaryRequirementGreaterThanZero"
            class="mb-4 mt-4"
          >
            <div
              v-if="requirementsExceedSomeProductQuantities"
              class="alert alert-warning"
              role="alert"
            >
              <small>Some products have been hidden because the max quantities were exceeded</small>
            </div>
            <SelectProduct
              :disabled="bookingReserved || reserveInProgress || (editMode && paymentComplete)"
              :product-name="'Lounges'"
            />
          </div>
          <div
            v-if="loungeSelected"
            class="mb-4 mt-4"
          >
            <SelectTravelExtras
              :disabled="bookingReserved || reserveInProgress || (editMode && paymentComplete)"
            />
            <div>
              <button
                v-if="!bookingReserved"
                :disabled="updating || reserveInProgress"
                type="button"
                class="btn btn-lg btn-primary mt-4"
                @click="reserveBooking()"
              >
                Reserve Booking
              </button>
            </div>
            <div v-if="bookingReserved">
              <CustomerDetails :disabled="bookingConfirmed" />
              <div v-if="validBookingDetails && customerInfoValid">
                <PaymentDetails
                  v-if="!hidePayment"
                  :disabled="updating || !validBookingDetails || confirmFailed || reserveInProgress || bookingConfirmed"
                  :reserve-in-progress="reserveInProgress"
                />
                <div v-if="hidePayment">
                  <button
                    v-if="total !== 0"
                    :disabled="updating || bookingConfirmed"
                    type="button"
                    class="btn btn-lg btn-primary mt-4"
                    @click="submitBooking"
                  >
                    <div v-if="updating">
                      <i class="btn-loading__icon fa fa-spinner fa-spin" />
                    </div>
                    <span v-if="!updating || bookingConfirmed">
                      Modify
                      Booking
                    </span>
                  </button>
                  <button
                    v-if="total === 0"
                    :disabled="updating || bookingConfirmed"
                    type="button"
                    class="btn btn-lg btn-primary mt-4"
                    @click="submitBooking"
                  >
                    Create Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4 sticky-content">
          <div class="col-heading">
            <h3 class="mb-4">
              Booking summary
            </h3>
          </div>
          <div class="card">
            <div class="card-body">
              <div
                v-if="editMode && bookingProduct"
                class="section--dim"
              >
                <h3>Original Booking</h3>
                <div class="mt-3">
                  <booking-summary-details
                    :hide-price-and-product="true"
                    :booking="bookingProduct"
                  />
                  <booking-summary-extras
                    :extras="bookingProductExtras"
                  />
                  <div class="col-md-12 content-list mb-2 mt-3">
                    <b>Total price:</b>
                    <strong>
                      {{ $n(bookingProduct.totalPrice, 'currency', languageCode) }}
                    </strong>
                  </div>
                </div>
              </div>

              <h3 :class="{'mt-4': editMode}">
                Current Booking
              </h3>
              <div
                v-if="getSelectedAirport"
                class="row"
              >
                <div class="col-md-12 content-list mb-2">
                  <b>Airport:</b>
                  <span>{{ getSelectedAirport.name }}</span>
                </div>
              </div>
              <div
                v-if="selectedEntryDateTime"
                class="row"
              >
                <div class="col-md-12 content-list mb-2">
                  <b>Entry Date:</b>
                  <span>{{ localDateTime(selectedEntryDateTime) }}</span>
                </div>
              </div>
              <booking-summary-product
                v-if="loungeSelected"
                :discrepancies="productsWithDiscrepancies"
                :product="selectedProduct"
              />
              <booking-summary-extras
                :discrepancies="extrasWithDiscrepancies"
                :extras="selectedExtras"
              />
              <booking-summary-promo-code
                v-if="!editMode"
                :booking="selectedProduct"
                :disabled="!airportSelected || !terminalSelected || loadingProducts || bookingReserved"
                :code-valid="promoValid"
                :pending="loadingProducts"
                :code="promoCode"
                @codeReset="resetPromoCode"
                @codeApplied="applyPromoCode"
              />
              <div class="row">
                <div class="col-12 content-list price-total">
                  <b>Total price:</b>
                  <span v-if="bookingReserved">
                    {{ $n(bookingReserveResponse.data.booking.price, 'currency', languageCode) }}
                  </span>
                  <span v-else>
                    {{ $n(total, 'currency', languageCode) }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="confirmFailed"
              class="mt-2 text--error"
            >
              <span>Booking could not be confirmed, please re-reserve before continuing to ensure the correct pricing</span>
              <button
                :disabled="updating || reserveInProgress"
                type="button"
                class="btn btn-block btn-lg btn-primary mt-4"
                @click="reserveBooking()"
              >
                Re-reserve Booking
              </button>
            </div>
          </div>
        </div>
      </div>
      <BookingResult
        v-if="showBookingResult"
        :booking-result="lastCompletedBookingCache"
      />
    </div>
  </div>
</template>

<script>
import SelectAirport from '../SelectAirport'
import SelectDateTime from './SelectDateTime'
import SelectTerminal from './SelectTerminal'
import SelectProduct from './SelectProduct'
import ProductRequirement from './ProductRequirement'
import SelectTravelExtras from './SelectTravelExtras'
import CustomerDetails from './CustomerDetails'
import PaymentDetails from './PaymentDetails'
import BookingResult from './BookingResult'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import BookingSummaryDetails from './booking-summary/BookingSummaryDetails'
import BookingSummaryExtras from './booking-summary/BookingSummaryExtras'
import BookingSummaryProduct from './booking-summary/BookingSummaryProduct'
import BookingSummaryPromoCode from './booking-summary/BookingSummaryPromoCode'
import _ from 'lodash'
export default {
    name: 'BookingCreateLoungePage',
    components: {
        SelectAirport,
        SelectDateTime,
        SelectTerminal,
        SelectProduct,
        ProductRequirement,
        SelectTravelExtras,
        CustomerDetails,
        PaymentDetails,
        BookingResult,
        BookingSummaryDetails,
        BookingSummaryExtras,
        BookingSummaryProduct,
        BookingSummaryPromoCode
    },
    data () {
        return {
            reserveInProgress: false,
            paymentComplete: false,
            marketingRequested: false,
            termsAccepted: false,
            loadingProducts: false,
            timePickerConfig: {
                start: '06:00',
                interval: 15,
                period: 'minutes'
            },
            requirementConfigs: [
                {
                    id: 'primary',
                    label: 'Adults (16+)',
                    maxQuantity: 50
                },
                {
                    id: 'secondary',
                    label: 'Children (2+)',
                    maxQuantity: 50
                }
            ],
            editMode: false,
            showBookingResponse: false,
            lastCompletedBookingCache: {},
            productsWithDiscrepancies: [],
            extrasWithDiscrepancies: [],
            bookingId: this.$route.params.bookingId,
            originalLounge: null,
            originalExtras: [],
            promoCode: '',
            promoValid: false,
            promoApplicationAttempted: false,
            backPath: '',
            updating: false,
            loadingExistingBooking: false,
            confirmFailed: false
        }
    },
    computed: {
        ...mapState({
            selectedAirportId: state => state.airport.selectedAirportId,
            selectedTerminalId: state => state.airport.selectedTerminalId,
            selectedEntryDateTime: state => state.booking.entryAndExit.entryDateTime,
            selectedExitDateTime: state => state.booking.entryAndExit.exitDateTime,
            selectedDateTimeValid: state => state.booking.entryAndExit.validSelection,
            selectedProductId: state => state.product.selectedProductId,
            bookingConfirmResponse: state => state.booking.bookingConfirmResponse,
            bookingReserveResponse: state => state.booking.bookingReserveResponse,
            customer: state => state.booking.customer,
            extras: state => state.extra.extras,
            errors: state => state.booking.error,
            selectedExtras: state => state.extra.extras,
            paymentSession: state => state.booking.paymentSession,
            primaryRequirement: state =>
                state.product.requirements.find(r => r.id === 'primary'),
            secondaryRequirement: state =>
                state.product.requirements.find(r => r.id === 'secondary'),
            bookingProduct: state => state.booking.product,
            bookingProductExtras: state => state.booking.productExtras
        }),
        ...mapGetters({
            getSelectedAirport: 'getSelectedAirport',
            requirementsExceedSomeProductQuantities:
                'product/requirementsExceedSomeProductQuantities',
            getProductsWithAvailableQuantity:
                'product/getProductsWithAvailableQuantity'
        }),
        airportSelected () {
            return this.selectedAirportId !== 0
        },
        terminalSelected () {
            return this.selectedTerminalId !== 0
        },
        validBookingDetails () {
            return this.$store.state.booking.newBookingFormValid
        },
        promoApplied () {
            const parkingPromoApplied = this.$store.state.product.promoApplied
            const extrasPromoApplied = false // return this.$store.state.extras.promoApplied
            return parkingPromoApplied || extrasPromoApplied
        },
        entrySelected () {
            return this.selectedEntryDateTime !== null
        },
        primaryRequirementGreaterThanZero () {
            return this.primaryRequirement.quantity > 0
        },
        secondaryRequirementGreaterThanZero () {
            return this.secondaryRequirement.quantity > 0
        },
        loungeSelected () {
            return this.selectedProductId !== null
        },
        selectedProduct: {
            get () {
                if (!this.loungeSelected) return { name: 'n/a', totalPrice: 0 }
                return this.$store.getters['product/getSelectedProduct'](
                    this.selectedProductId
                )
            }
        },
        showBookingResult () {
            return this.showBookingResponse || this.bookingConfirmResponse !== null || this.errors
        },
        total () {
            let extrasPrice = 0
            if (this.selectedExtras.length > 0) {
                extrasPrice = this.selectedExtras.reduce((sum, e) => {
                    sum += e.totalPrice
                    return sum
                }, 0)
            }
            return this.selectedProduct.totalPrice + extrasPrice
        },
        hidePayment () {
            var price = this.bookingReserveResponse.data.booking.price
            if (this.bookingProduct) {
                return this.bookingProduct.totalPrice === price
            } else {
                return price === 0
            }
        },
        customerInfoValid () {
            return (
                this.customer.firstName !== null &&
                this.customer.surname !== null &&
                this.customer.email !== null
            )
        },
        bookingReserved () {
            if (this.bookingReserveResponse !== null && this.bookingReserveResponse.status === 201) {
                return true
            }
            return false
        },
        bookingConfirmed () {
            if (this.bookingConfirmResponse !== null && this.bookingConfirmResponse.status === 201 && this.bookingConfirmResponse.data.booking.status === 1) {
                return true
            }
            return false
        },
        languageCode () {
            if (!this.$store.state.airport.selectedAirportId) {
                let languageCode = this.$store.getters['getSpecificConfiguration']({
                    section: 'CultureSettings',
                    identifier: 'LanguageCode'
                })

                if (languageCode) {
                    return languageCode.languageCode
                }
            }

            var code = this.$store.getters['getLanguageCode']({
                identifier: this.$store.state.airport.selectedAirportId
            })
            if (code !== null) {
                return code
            }
            return 'en-GB'
        },
        timeZone () {
            var airportConfig = this.$store.getters['getSpecificConfiguration']({
                section: 'AirportConfigurations',
                identifier: this.$store.getters['getSelectedAirport'].id
            })
            if (airportConfig && airportConfig.cultureSettings.timeZone) {
                return airportConfig.cultureSettings.timeZone
            }
            return 'Europe/London'
        }
    },
    watch: {
        selectedAirportId () {
            this.getConfiguration()
            this.resetProduct()
        },
        selectedTerminalId () {
            this.resetProduct()
            if (this.airportSelected && this.terminalSelected && this.primaryRequirementGreaterThanZero) {
                this.getLounges()
            }
        },
        selectedEntryDateTime () {
            this.resetProduct()
            if (
                this.airportSelected &&
                this.terminalSelected &&
                this.entrySelected &&
                this.primaryRequirementGreaterThanZero
            ) {
                this.resetReservation()
                this.getLounges()
            }
        },
        'primaryRequirement.quantity' () {
            this.resetProduct()
            if (
                this.airportSelected &&
                this.terminalSelected &&
                this.entrySelected &&
                this.primaryRequirementGreaterThanZero
            ) {
                this.resetReservation()
                this.getLounges()
            }
        },
        'secondaryRequirement.quantity' () {
            this.resetProduct()
            if (
                this.airportSelected &&
                this.terminalSelected &&
                this.entrySelected &&
                this.primaryRequirementGreaterThanZero
            ) {
                this.resetReservation()
                this.getLounges()
            }
        },
        selectedProductId () {
            this.resetExtras()
            if (this.loungeSelected) this.getExtras()
        },
        paymentSession () {
            if (this.paymentSession !== null) this.submitBooking()
        }
    },
    async created () {
        this.initialise()
        this.resetBooking()

        if (this.bookingId) {
            this.loadingExistingBooking = true

            this.backPath = `/booking/${this.bookingId}/`
            this.editMode = true
            await this.$store.dispatch('getProduct', this.bookingId)
            this.promoCode = this.bookingProduct.promoCode
            await this.$store.dispatch(
                'setSelectedAirportId',
                this.bookingProduct.airportId
            )

            if (this.bookingProduct.terminalId > 0) {
                await this.$store.commit(
                    'setSelectedTerminalId',
                    this.bookingProduct.terminalId
                )
            }

            var entryDate = new Date(this.bookingProduct.entryDate)
            await this.$store.dispatch('updateEntryDate', entryDate)
            await this.$store.dispatch('updateEntryDateTime', entryDate)
            await this.$store.dispatch('updateDateTimeValid', true)
            await this.$store.dispatch(
                'updateEntryTime',
                moment(entryDate).utc().format('HH:mm')
            )

            await this.$store.dispatch('getProductExtras', this.bookingId)
            await this.$store.dispatch('getCustomer', this.bookingId)

            this.originalLounge = this.bookingProductExtras.filter(x => {
                return x.addonGroupId === 4
            })[0]

            this.originalExtras = this.bookingProductExtras.filter(x => {
                return x.addonGroupId !== 4
            })
            this.loadingExistingBooking = false
            this.promoCode = this.bookingProduct.promoCode
            this.applyPromoCode(this.promoCode)

            this.$forceUpdate()
        }
    },
    beforeDestroy () {
        this.resetBooking()
    },
    methods: {
        localDateTime (dateTime) {
            var momentDateTime = moment.utc(dateTime).format('ddd MMM DD YYYY HH:mm:ss')
            var localDateTime = moment(momentDateTime).local().format('ddd MMM DD YYYY HH:mm:ss')

            return localDateTime
        },
        promoApplicationCheck () {
            if (this.promoCode !== null && this.promoCode.length > 0) {
                this.promoApplicationAttempted = true
                this.promoValid = this.promoApplied === true
            }
        },
        async applyPromoCode (code) {
            this.promoCode = code
            if (!this.airportSelected || !this.terminalSelected || !this.primaryRequirementGreaterThanZero) return
            await this.getLounges()
            await this.getExtras()
        },
        async resetPromoCode () {
            this.loadingProducts = true
            this.promoCode = null
            this.promoApplicationAttempted = false
            await this.getLounges()
            await this.getExtras()
            this.loadingProducts = false
        },
        resetRequirements () {
            this.$store.commit('product/resetRequirements')
        },
        resetProduct () {
            this.$store.commit('product/reset')
        },
        resetExtras () {
            this.$store.commit('resetExtras')
        },
        resetBooking () {
            this.$store.commit('resetSelectedAirportAndTerminal')
            this.resetRequirements()
            this.resetProduct()
            this.resetExtras()
            this.productsWithDiscrepancies = []
            this.extrasWithDiscrepancies = []
            this.$store.commit('setVehicleRegistration', null)
            this.$store.commit('resetBooking') // resets entry/exit, customer, payment session and booking response
        },
        async initialise () {
            this.$store.dispatch('clearBookingData')
            await this.$store.dispatch('getAirports')
        },
        async getLounges () {
            if (this.loadingExistingBooking) return
            this.showBookingResponse = false
            this.loadingProducts = true
            const airport = this.getSelectedAirport
            await this.$store.dispatch('product/getLoungeAvailability', {
                airportCode: airport.code,
                terminalId: this.selectedTerminalId,
                entryDateTime: this.selectedEntryDateTime,
                quantities: [
                    this.primaryRequirement.quantity,
                    this.secondaryRequirement.quantity
                ],
                promoCode: this.promoCode
            })
            this.promoApplicationCheck()
            this.loadingProducts = false
        },
        getExtras: async function () {
            this.loadingProducts = true
            const airport = this.getSelectedAirport
            const product = this.selectedProduct
            await this.$store.dispatch('getExtraAvailability', {
                airportCode: airport.code,
                terminalId: this.selectedTerminalId,
                entryDateTime: this.selectedEntryDateTime,
                categoryId: product.categoryId,
                quantities: [
                    this.primaryRequirement.quantity,
                    this.secondaryRequirement.quantity
                ],
                travelDirection: 'Outbound',
                promoCode: this.promoCode
            })
            this.promoApplicationCheck()

            if (this.editMode) {
                this.originalExtras.forEach(e => {
                    e.offerLines.forEach(o => {
                        this.$store.commit('updateExtraBySummary', {
                            extraId: e.id,
                            offerLineId: o.offerlineId,
                            quantity: o.quantity
                        })
                    })
                })

                this.$forceUpdate()
            }
            this.loadingProducts = false
        },
        async submitBooking () {
            if (
                this.customer.firstName === null ||
                this.customer.surname === null ||
                this.customer.email === null
            ) {
                this.$toasted.error('Missing Customer Details')
                return
            }
            this.updateBooking()
            this.updating = true
            this.confirmFailed = false
            if (!this.editMode) {
                var result = await this.$store.dispatch('confirmBooking')
                if (result.status === 200 || result.status === 201) {
                    this.$toasted.success('Confirmed')
                    this.showBookingResponse = true
                    this.lastCompletedBookingCache = { booking: _.cloneDeep(this.$store.state.booking.booking), bookingConfirmResponse: _.cloneDeep(this.bookingConfirmResponse), error: _.cloneDeep(this.errors) }
                    this.resetBooking()
                    let params = Object.assign({}, this.$route.params)
                    delete params.bookingId
                    this.$router.replace({ params })
                    this.editMode = false
                    this.$store.dispatch('resetVehicle')
                } else {
                    this.confirmFailed = true
                    this.showBookingResponse = true
                    this.lastCompletedBookingCache = { bookingConfirmResponse: _.cloneDeep(this.bookingConfirmResponse), error: _.cloneDeep(this.errors) }
                }
                this.updating = false
            } else {
                result = await this.$store.dispatch('confirmModifyBooking')
                if (result.status === 200 || result.status === 201) {
                    this.$toasted.success('Confirmed')
                    this.showBookingResponse = true
                    this.lastCompletedBookingCache = { booking: _.cloneDeep(this.$store.state.booking.booking), bookingConfirmResponse: _.cloneDeep(this.bookingConfirmResponse), error: _.cloneDeep(this.errors) }
                    this.paymentComplete = true
                } else {
                    this.confirmFailed = true
                    this.showBookingResponse = true
                    this.lastCompletedBookingCache = { bookingConfirmResponse: _.cloneDeep(this.bookingConfirmResponse), error: _.cloneDeep(this.errors) }
                }
                this.updating = false
            }
        },
        async getConfiguration () {
            const airport = this.getSelectedAirport
            if (airport !== undefined) {
                await this.$store.dispatch('getConfiguration', {
                    section: 'PaymentConfigurations',
                    identifier: airport.code
                })
                await this.$store.dispatch('getConfiguration', {
                    section: 'AirportConfigurations',
                    identifier: airport.id
                })
            }
        },
        async reserveBooking () {
            if (this.reserveInProgress) return
            if (this.editMode) {
                this.bookingId = this.$route.params.bookingId
            }
            this.productsWithDiscrepancies.length = 0
            this.extrasWithDiscrepancies.length = 0
            this.confirmFailed = false
            this.reserveInProgress = true
            this.updateBooking()
            let result = null
            if (!this.editMode) {
                result = await this.$store.dispatch('reserveBooking')
            } else {
                result = await this.$store.dispatch('modifyBooking')
            }
            if (result.status === 200 || result.status === 201) {
                let successMessage = 'Reserved'
                if (result.data.booking.price !== this.total) {
                    successMessage = `${successMessage}. Some prices may have changed! Review booking details.`
                }
                this.$toasted.success(successMessage)
                this.bookingId = result.data.booking.id
                this.updateSelectedBookingProducts(result.data.booking)
            } else {
                this.$toasted.error('Reservation Failed')
            }
            this.reserveInProgress = false
        },
        updateSelectedBookingProducts (reservedBooking) {
            reservedBooking.items.parkings.forEach((reservedProduct) => {
                if (this.selectedProduct.id.toString() === reservedProduct.productId.toString() && this.selectedProduct.price !== reservedProduct.amount) {
                    this.getLounges()
                    var discrepancyType = (this.selectedProduct.price - reservedProduct.amount) > 0 ? 'decreased' : 'increased'
                    this.productsWithDiscrepancies.push({ id: this.selectedProduct.id, reservedPrice: reservedProduct.amount, oldPrice: this.selectedProduct.price, type: discrepancyType })
                }
            })

            this.selectedExtras.forEach((selectedExtra) => {
                reservedBooking.items.extras.forEach((reservedExtra) => {
                    if (selectedExtra.id.toString() === reservedExtra.extraId.toString()) {
                        var selectedExtrasTotal = 0
                        var reservedExtrasTotal = 0
                        selectedExtra.offerLines.forEach((offerLine) => {
                            selectedExtrasTotal += offerLine.totalPrice
                        })
                        reservedExtra.offerLines.forEach((offerLine) => {
                            reservedExtrasTotal += offerLine.amount
                        })
                        if (selectedExtrasTotal !== reservedExtrasTotal) {
                            this.getExtras()
                            var discrepancyType = (selectedExtra.totalPrice - reservedExtra.amount) > 0 ? 'decreased' : 'increased'
                            this.extrasWithDiscrepancies.push({ id: selectedExtra.id, reservedPrice: reservedExtra.amount, oldPrice: selectedExtra.totalPrice, type: discrepancyType })
                        }
                    }
                })
            })
        },
        updateBooking () {
            const product = this.selectedProduct
            product.offerLines = product.offerLines.filter(c => c.quantity > 0)
            var extras = Array.from(this.selectedExtras)
            let extrasToRemove = []
            for (let i = 0; i < extras.length; i++) {
                if (extras[i].offerLines && extras[i].offerLines.filter(c => c.quantity > 0).length > 0) {
                    extras[i].offerLines = extras[i].offerLines.filter(c => c.quantity > 0)
                } else {
                    extrasToRemove.push(extras[i].id)
                }
            }
            for (let i = 0; i < extrasToRemove.length; i++) {
                let index = extras.map(function (extra) {
                    return extra.id
                }).indexOf(extrasToRemove[i])
                extras.splice(index, 1)
            }
            this.$store.commit('updateBooking', {
                airportCode: this.getSelectedAirport.code,
                entryDateTime: this.selectedEntryDateTime,
                exitDateTime: this.selectedEntryDateTime,
                promotionCode: this.promoCode,
                affiliateCode: null,
                terminalId: this.selectedTerminalId,
                extras: [product, ...extras],
                customer: this.customer,
                paymentSession: this.paymentSession,
                hasAcceptedTerms: false,
                bookingId: this.bookingId
            })
        },
        resetReservation () {
            this.bookingId = this.$route.params.bookingId
            this.$store.dispatch('resetBookingReserveResponse')
        }
    }
}
</script>
